import {React, useState, useEffect, useRef} from 'react';
import styles from './MapView.module.css';

const renderDirectionSVG = (directionsParam) => {
  const directions = Array.isArray(directionsParam)
    ? directionsParam
    : (typeof directionsParam === 'string' && directionsParam.length > 0
        ? directionsParam.split('-')
        : []);
  // Chỉ thẳng
  if (directions.length === 1 && directions[0] === 'up') {
    return (
      <img src="/static/icon-direction/up.svg" width="24" height="24" alt="up" />
    );
  }
    // Chỉ dưới
  if (directions.length === 1 && directions[0] === 'down') {
    return (
        <img src="/static/icon-direction/down.svg" width="24" height="24" alt="down" />
    );
  }
  // Chỉ phải
  if (directions.length === 1 && directions[0] === 'right') {
    return (
      <img src="/static/icon-direction/right.svg" width="24" height="24" alt="right" />
    );
  }
  // Chỉ trái
  if (directions.length === 1 && directions[0] === 'left') {
    return (
      <img src="/static/icon-direction/left.svg" width="24" height="24" alt="left" />
    );
  }
  // Thẳng + phải
  if (directions.includes('up') && directions.includes('right')) {
    return (
      <img src="/static/icon-direction/up-right.svg" width="24" height="24" alt="up-right" />
    );
  }
  // Thẳng + trái
  if (directions.includes('up') && directions.includes('left')) {
    console.log('Rendering up-left direction');
    return (
        <img src="/static/icon-direction/up-left.svg" width="24" height="24" alt="up-left" />
    );
  }
  // Dưới + phải
  if (directions.includes('down') && directions.includes('right')) {
    return (
      <img src="/static/icon-direction/down-right.svg" width="24" height="24" alt="down-right" />
    );
  }
    // Dưới + trái
    if (directions.includes('down') && directions.includes('left')) {
    return (
      <img src="/static/icon-direction/down-left.svg" width="24" height="24" alt="down-left" />
    );
    }
    // Ngược + Trái
    if (directions.includes('left') && directions.includes('reversed')) {
        return (
            <img src="/static/icon-direction/left-reverser.svg" width="24" height="24" alt="left-reversed" />
        );
    }
    // Ngược + Phải
    if (directions.includes('right') && directions.includes('reversed')) {
        return (
            <img src="/static/icon-direction/right-reverser.svg" width="24" height="24" alt="right-reversed" />
        );
    }
 return (
    <span style={{ color: 'red' }}></span>
);
};

const MapView = ({ 
    layout, 
    devices,
    selectedDevice, 
    selectedLine,
    onDeviceSelect,
    zoom,
    pan,
    isDragging,
    onZoomIn,
    onZoomOut,
    onZoomReset,
    onWheel,
    onMouseDown,
    onImageLoad,
    imageContainerRef,
    onMousePositionChange,
    mousePosition,
    onMapClick,
    clickedPosition,
    filter,
    onDevicePositionChange,
    onCancelPositionChange,
}) => {
    const [draggingDeviceId, setDraggingDeviceId] = useState(null);
    const [dragModeDeviceId, setDragModeDeviceId] = useState(null);
    const [pendingPosition, setPendingPosition] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    const clickTimeout = useRef(null);

    const calculateDevicePosition = (device, imageSize) => {
        // Check if device has valid position data
        if (!device.position || typeof device.position.x === 'undefined' || typeof device.position.y === 'undefined') {
            return { left: '50%', top: '50%' };
        }
        
        if (!imageSize.width || !imageSize.height) {
            return { left: '50%', top: '50%' };
        }
        
        // Calculate position as percentage of image size
        const leftPercent = (device.position.x / imageSize.width) * 100;
        const topPercent = (device.position.y / imageSize.height) * 100;
        
        return {
            left: `${leftPercent}%`,
            top: `${topPercent}%`
        };
    };

    const handleDeviceClick = (device, e) => {
        if (e) {
            e.stopPropagation();
        }
        
        // If device is in drag mode, don't select
        if (dragModeDeviceId === device._id) {
            return;
        }
        
        // Clear any existing timeout
        if (clickTimeout.current) {
            clearTimeout(clickTimeout.current);
            clickTimeout.current = null;
        }
        
        // Set timeout for single click
        clickTimeout.current = setTimeout(() => {
            onDeviceSelect(device);
            clickTimeout.current = null;
        }, 300); // 300ms delay to distinguish from double-click
    };

    const handleDeviceDoubleClick = (device, e) => {
        if (e) {
            e.stopPropagation();
        }
        
        // Clear single click timeout
        if (clickTimeout.current) {
            clearTimeout(clickTimeout.current);
            clickTimeout.current = null;
        }
        
        // Toggle drag mode for this device
        if (dragModeDeviceId === device._id) {
            setDragModeDeviceId(null);
        } else {
            setDragModeDeviceId(device._id);
            onDeviceSelect(device);
        }
    };

    const handleMouseMove = (e) => {
        if (!layout.mapImage || !onMousePositionChange) return;
        
        const rect = e.currentTarget.getBoundingClientRect();
        const imageElement = e.currentTarget.querySelector('img');
        
        if (!imageElement) return;
        
        const imageRect = imageElement.getBoundingClientRect();
        
        // Calculate mouse position relative to image
        const x = e.clientX - imageRect.left;
        const y = e.clientY - imageRect.top;
        
        // Calculate actual coordinates based on image size and layout size
        const imageNaturalWidth = imageElement.naturalWidth;
        const imageNaturalHeight = imageElement.naturalHeight;
        const imageDisplayWidth = imageRect.width;
        const imageDisplayHeight = imageRect.height;
        
        // Convert to actual layout coordinates
        const actualX = Math.round((x / imageDisplayWidth) * (layout.size?.width || imageNaturalWidth));
        const actualY = Math.round((y / imageDisplayHeight) * (layout.size?.height || imageNaturalHeight));
        
        // Only report coordinates if mouse is within image bounds
        if (x >= 0 && x <= imageDisplayWidth && y >= 0 && y <= imageDisplayHeight) {
            onMousePositionChange({ x: actualX, y: actualY });
        } else {
            onMousePositionChange(null);
        }
    };

    const handleDragStart = (e, device) => {
        // Only allow drag if device is in drag mode
        if (dragModeDeviceId !== device._id) {
            return;
        }
        
        e.stopPropagation();
        setDraggingDeviceId(device._id);

        const imageContainer = imageContainerRef?.current;
        if (!imageContainer) return;

        const imageElement = imageContainer.querySelector('img');
        if (!imageElement) return;

        const imageRect = imageElement.getBoundingClientRect();

        const deviceX = e.clientX - imageRect.left;
        const deviceY = e.clientY - imageRect.top;

        dragOffset.current = { x: deviceX, y: deviceY };
    };

    const handleDragging = (e) => {
        if (!draggingDeviceId || !layout?.mapImage) return;

        const imageElement = imageContainerRef.current?.querySelector('img');
        const imageRect = imageElement.getBoundingClientRect();

        const x = e.clientX - imageRect.left - dragOffset.current.x;
        const y = e.clientY - imageRect.top - dragOffset.current.y;

        const imageDisplayWidth = imageRect.width;
        const imageDisplayHeight = imageRect.height;

        const naturalWidth = imageElement.naturalWidth;
        const naturalHeight = imageElement.naturalHeight;

        const layoutWidth = layout.size?.width || naturalWidth;
        const layoutHeight = layout.size?.height || naturalHeight;

        const percentX = (x + dragOffset.current.x) / imageDisplayWidth;
        const percentY = (y + dragOffset.current.y) / imageDisplayHeight;

        const newX = Math.max(0, Math.min(layoutWidth, Math.round(percentX * layoutWidth)));
        const newY = Math.max(0, Math.min(layoutHeight, Math.round(percentY * layoutHeight)));

        // Update position immediately for visual feedback
        if (onDevicePositionChange) {
            onDevicePositionChange(draggingDeviceId, { x: newX, y: newY });
            setPendingPosition({
                deviceId: draggingDeviceId,
                position: { x: newX, y: newY },
                deviceName: devices?.find(d => d._id === draggingDeviceId)?.name || 'Unknown Device'
            });
        }
    };

    const handleDragEnd = () => {
        if (draggingDeviceId) {
            // Get the device that was dragged
            const draggedDevice = devices?.find(d => d._id === draggingDeviceId);
            if (draggedDevice) {
                setShowConfirmDialog(true);
            }
        }
        setDraggingDeviceId(null);
    };

    const handleConfirmSave = async () => {
        if (pendingPosition && onDevicePositionChange) {
            try {
                // Call API to save the position
                await onDevicePositionChange(pendingPosition.deviceId, pendingPosition.position, true);
                setShowConfirmDialog(false);
                setPendingPosition(null);
                setDragModeDeviceId(null); // Exit drag mode after saving
            } catch (error) {
                console.error('Error saving device position:', error);
                alert('Failed to save device position. Please try again.');
            }
        }
    };

    const handleCancelSave = () => {
        if (pendingPosition && onCancelPositionChange) {
            // Revert to original position
            onCancelPositionChange(pendingPosition.deviceId);
        }
        setShowConfirmDialog(false);
        setPendingPosition(null);
        setDragModeDeviceId(null); // Exit drag mode on cancel
    };

        const getTypeIcon = (type, directionDisplay) => {
        if (type === 'light') return '💡';
        // Signal: render based on directionDisplay (string like 'up-right' or array)
        return (
            <span style={{ display: 'inline-flex', gap: 4 }}>
                {renderDirectionSVG(directionDisplay)}
            </span>
        );
    };

    useEffect(() => {
        if (draggingDeviceId) {
            window.addEventListener('mousemove', handleDragging);
            window.addEventListener('mouseup', handleDragEnd);
        } else {
            window.removeEventListener('mousemove', handleDragging);
            window.removeEventListener('mouseup', handleDragEnd);
        }

        return () => {
            window.removeEventListener('mousemove', handleDragging);
            window.removeEventListener('mouseup', handleDragEnd);
        };
    }, [draggingDeviceId]);

    return (
        <div className={styles.mapSection}>
            {/* Zoom Controls */}
            <div className={styles.zoomControls}>
                <button 
                    className={styles.zoomButton}
                    onClick={onZoomIn}
                    title="Zoom In"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="M21 21l-4.35-4.35"/>
                        <line x1="11" y1="8" x2="11" y2="14"/>
                        <line x1="8" y1="11" x2="14" y2="11"/>
                    </svg>
                </button>
                <span className={styles.zoomLevel}>{Math.round(zoom * 100)}%</span>
                <button 
                    className={styles.zoomButton}
                    onClick={onZoomOut}
                    title="Zoom Out"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="M21 21l-4.35-4.35"/>
                        <line x1="8" y1="11" x2="14" y2="11"/>
                    </svg>
                </button>
                <button 
                    className={styles.zoomButton}
                    onClick={onZoomReset}
                    title="Reset Zoom"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 4v6h6"/>
                        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                    </svg>
                </button>
            </div>

            {/* Drag Mode Indicator */}
            {dragModeDeviceId && (
                <div className={styles.dragModeIndicator}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2l3 3h-2v7h7v-2l3 3-3 3v-2h-7v7h2l-3 3-3-3h2v-7H4v2l-3-3 3-3v2h7V5h-2z"/>
                    </svg>
                    Drag mode active - Double-click device to exit
                </div>
            )}

            {/* Mouse Coordinates Display */}
            {mousePosition && (
                <div className={styles.mouseCoordinates}>
                    <span className={styles.coordLabel}>Position:</span>
                    <span className={styles.coordValue}>
                        X: {mousePosition.x}, Y: {mousePosition.y}
                    </span>
                </div>
            )}

            <div 
                className={styles.mapContainer}
                ref={imageContainerRef}
                onWheel={onWheel}
                onMouseDown={onMouseDown}
                onMouseMove={handleMouseMove}
                onMouseLeave={() => onMousePositionChange && onMousePositionChange(null)}
            >
                {layout.mapImage ? (
                    <div 
                        className={styles.mapWrapper}
                        style={{
                            transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
                            cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                        }}
                    >
                        <div className={styles.mapRelative}>
                            <img
                                src={`${process.env.REACT_APP_API_BASE_URL}${layout.mapImage}`}
                                alt={`${layout.name} Layout`}
                                className={styles.mapImage}
                                onLoad={onImageLoad}
                                onClick={onMapClick}
                                draggable={false}
                            />
                            {/* Device Points on Map */}
                            {devices?.filter(device => 
                                device && device.position && 
                                typeof device.position.x !== 'undefined' && 
                                typeof device.position.y !== 'undefined' &&
                                (filter === "all" || device.type === filter)
                            ).map((device) => {
                                const position = calculateDevicePosition(device, {
                                    width: layout.size?.width || 1000,
                                    height: layout.size?.height || 800
                                });
                                return (
                                    <div
                                        key={device._id}
                                        className={`${styles.devicePoint} 
                                            ${(selectedDevice?._id === device._id || selectedLine?.deviceIds.includes(device._id)) ? styles.selected : ''} 
                                            ${styles[device.status]} 
                                            ${styles[device.type]}
                                            ${device.isActive ? '' : styles.inactive}
                                            ${dragModeDeviceId === device._id ? styles.dragMode : ''}
                                            ${draggingDeviceId === device._id ? styles.dragging : ''}`}
                                        style={position}
                                        onMouseEnter={!dragModeDeviceId ? () => onDeviceSelect && onDeviceSelect(device) : undefined}
                                        onMouseDown={(e) => handleDragStart(e, device)}
                                        onClick={(e) => handleDeviceClick(device, e)}
                                        onDoubleClick={(e) => handleDeviceDoubleClick(device, e)}
                                        title={dragModeDeviceId === device._id ? 
                                            `${device.name} - Drag mode (double-click to exit)` : 
                                            `${device.name} (${device.type}) - Double-click to enable drag`}
                                    >
                                        <div className={styles.deviceIcon}>
                                            {getTypeIcon(device.type, device.directionDisplay)}
                                        </div>
                                        <div className={styles.deviceTooltip}>
                                            <div className={styles.tooltipName}>{device.name}</div>
                                            <div className={styles.tooltipStatus}>{device.status}</div>
                                            {device.type === 'light' && device.brightness !== undefined && (
                                                <div className={styles.tooltipBrightness}>
                                                    {device.brightness}%
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                                
                            {/* Clicked Position Indicator */}
                            {clickedPosition && (
                                <div
                                    className={styles.clickedPositionIndicator}
                                    style={{
                                        left: `${(clickedPosition.x / (layout.size?.width || 1000)) * 100}%`,
                                        top: `${(clickedPosition.y / (layout.size?.height || 800)) * 100}%`
                                    }}
                                    title={`Clicked Position: (${clickedPosition.x}, ${clickedPosition.y})`}
                                >
                                    <div className={styles.clickedIcon}>📍</div>
                                    <div className={styles.clickedTooltip}>
                                        Click "Add Device" to place here
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className={styles.noImage}>
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="9" cy="9" r="2"/>
                            <path d="M21 15l-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
                        </svg>
                        <p>No layout image available</p>
                        <button className={styles.uploadButton}>Upload Image</button>
                    </div>
                )}
            </div>

            {/* Confirm Save Position Dialog */}
            {showConfirmDialog && pendingPosition && (
                <div className={styles.confirmDialog}>
                    <div className={styles.dialogContent}>
                        <h3>Save Device Position?</h3>
                        <p>
                            Do you want to save the new position for <strong>{pendingPosition.deviceName}</strong>?
                        </p>
                        <p className={styles.positionInfo}>
                            New position: X: {pendingPosition.position?.x}, Y: {pendingPosition.position?.y}
                        </p>
                        <div className={styles.dialogActions}>
                            <button 
                                className={styles.cancelButton}
                                onClick={handleCancelSave}
                            >
                                Cancel
                            </button>
                            <button 
                                className={styles.saveButton}
                                onClick={handleConfirmSave}
                            >
                                Save Position
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MapView; 